/**
 * PATH: packages/ui/src/undo/undoStack.ts
 * FILE_ID: UI-UNDO-STACK-001
 * PURPOSE: Pure, framework-free client-side undo/redo stack.
 * LANDMINE: Promoted from kanban/undo/undoStack.ts. Kanban keeps a
 *      re-export shim. Do not copy this body back into kanban.
 * DEPENDENCIES: none (pure TS — unit-testable without React/jsdom).
 */

/** What kind of object an undo entry compensates on. Defaults to 'card'
 *  when omitted. QA callers omit scope or reuse these labels. */
export type UndoScope = 'card' | 'lane'

/** A single undoable action. `undo` (and optional `redo`) MUST route
 *  through the normal mutation pipe — never a raw cache write. */
export interface UndoEntry {
  /** Human copy for toasts, e.g. "Move card", "Rename area". */
  label: string
  /** See UndoScope — omit for card-scoped entries. */
  scope?: UndoScope
  /** Compensating mutation. Rejections are caught and reported. */
  undo: () => Promise<unknown>
  /** Re-apply the original action. Omit when re-applying is unsafe. */
  redo?: () => Promise<unknown>
}

export type UndoResult =
  | { ok: true; label: string; scope?: UndoScope }
  | { ok: false; label: string; scope?: UndoScope; error: unknown }
  | null

export interface UndoStack {
  push: (entry: UndoEntry) => void
  /** Pop + execute the newest entry. Resolves null when empty. */
  undo: () => Promise<UndoResult>
  /** Re-execute the most recently undone entry (when it declared redo). */
  redo: () => Promise<UndoResult>
  canUndo: () => boolean
  canRedo: () => boolean
  /** True while an undo/redo compensating mutation is in flight. Used to
   *  suppress re-registration from the compensating mutation's own
   *  onSuccess (otherwise Ctrl+Z Ctrl+Z would ping-pong one action
   *  instead of walking back through history). */
  isBusy: () => boolean
  clear: () => void
  size: () => number
}

export const UNDO_STACK_CAPACITY = 25

/**
 * f(capacity) -> a bounded LIFO undo stack with a redo pile.
 *
 * Failure semantics: if a compensating mutation rejects, the entry is NOT
 * restored to the stack — the server state is unknown and re-running the
 * same thunk blind risks double-compensation.
 */
export function createUndoStack(capacity: number = UNDO_STACK_CAPACITY): UndoStack {
  const entries: UndoEntry[] = []
  const redoPile: UndoEntry[] = []
  let busy = false

  return {
    push(entry: UndoEntry) {
      if (busy) return // compensating mutation re-entered — see isBusy()
      entries.push(entry)
      if (entries.length > capacity) entries.shift()
      // A fresh action invalidates the redo timeline (standard editor
      // semantics — you can't redo into a divergent history).
      redoPile.length = 0
    },

    async undo(): Promise<UndoResult> {
      if (busy) return null // single-flight: ignore key-repeat while executing
      const entry = entries.pop()
      if (!entry) return null
      busy = true
      try {
        await entry.undo()
        if (entry.redo) redoPile.push(entry)
        return { ok: true, label: entry.label, scope: entry.scope }
      } catch (error) {
        return { ok: false, label: entry.label, scope: entry.scope, error }
      } finally {
        busy = false
      }
    },

    async redo(): Promise<UndoResult> {
      if (busy) return null
      const entry = redoPile.pop()
      if (!entry || !entry.redo) return null
      busy = true
      try {
        await entry.redo()
        entries.push(entry)
        if (entries.length > capacity) entries.shift()
        return { ok: true, label: entry.label, scope: entry.scope }
      } catch (error) {
        return { ok: false, label: entry.label, scope: entry.scope, error }
      } finally {
        busy = false
      }
    },

    canUndo: () => entries.length > 0,
    canRedo: () => redoPile.length > 0,
    isBusy: () => busy,
    clear() {
      entries.length = 0
      redoPile.length = 0
    },
    size: () => entries.length,
  }
}
